<template>
  <div class="container-fluid mt-3 mi-dia">
    <!-- Cabecera -->
    <header class="md-hero">
      <div>
        <h2 class="md-hero__title">{{ saludoTexto }}<span v-if="nombre">, {{ nombre }}</span></h2>
        <p class="md-hero__fecha">{{ fechaLegible }}</p>
      </div>
      <button class="btn btn-light btn-sm md-refresh" :disabled="cargando || cargandoTick" @click="cargar(true)">
        <span v-if="cargando || cargandoTick" class="spinner-border spinner-border-sm"></span>
        <i v-else class="bi bi-arrow-clockwise"></i>
        <span class="d-none d-sm-inline ms-1">Actualizar</span>
      </button>
    </header>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-if="errorAccion" class="alert alert-warning alert-dismissible py-2 small">
      {{ errorAccion }}
      <button type="button" class="btn-close" aria-label="Cerrar" @click="errorAccion = ''"></button>
    </div>

    <div v-if="cargando && !datos" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
    </div>

    <template v-else-if="datos">
      <!-- Resumen rápido -->
      <div class="md-chips">
        <a v-if="tick.configured" class="md-chip" href="#md-tareas">
          <i class="bi bi-check2-square"></i>
          <strong>{{ tareasPendientes.length }}</strong> tareas
        </a>
        <a v-if="tick.configured" class="md-chip" href="#md-habitos">
          <i class="bi bi-lightning-charge"></i>
          <strong>{{ tick.habitosCargando ? '…' : habitosPendientes.length }}</strong> hábitos
        </a>
        <router-link
          v-if="correo.total > 0"
          class="md-chip"
          :to="correo.sinLeer ? { path: '/gmail', query: { filtro: 'sinleer' } } : '/gmail'"
          title="Bandeja de entrada: recibidos sin archivar de ayer y hoy"
        >
          <i class="bi bi-envelope"></i>
          <strong>{{ correo.mas ? correo.total + '+' : correo.total }}</strong> {{ correo.total === 1 ? 'correo' : 'correos' }}
          <span v-if="correo.sinLeer" class="md-chip__alerta">{{ correo.sinLeer }} sin leer</span>
        </router-link>
        <router-link v-if="esAdmin || datos.pedidos.activos > 0" class="md-chip" to="/seguimiento">
          <i class="bi bi-truck"></i>
          <strong>{{ datos.pedidos.activos }}</strong> paquetes
          <span v-if="datos.pedidos.reclamar" class="md-chip__alerta">{{ datos.pedidos.reclamar }} a reclamar</span>
        </router-link>
        <router-link v-if="esAdmin || datos.peliculas.length" class="md-chip" to="/peliculas">
          <i class="bi bi-film"></i>
          <strong>{{ datos.peliculas.length }}</strong> viendo
        </router-link>
      </div>

      <!-- Bienvenida: usuarios que aún no han conectado ningún servicio de agenda -->
      <section v-if="!esAdmin && !integraciones.ticktick && !integraciones.calendar" class="md-card md-intro">
        <h5 class="mb-1"><i class="bi bi-stars me-2"></i>Tu día, en un solo sitio</h5>
        <p class="mb-2 text-muted">
          Conecta tus servicios y aquí verás lo de hoy: tus tareas y hábitos de TickTick y los eventos de tu
          Google Calendar. Cada uno se conecta con tu propia cuenta.
        </p>
        <router-link to="/perfil" class="btn btn-primary btn-sm">Conectar en Perfil</router-link>
      </section>

      <div class="row g-3 mt-0">
        <!-- Columna principal -->
        <div class="col-lg-7 d-flex flex-column gap-3">
          <!-- Agenda de Google Calendar: cada usuario con su cuenta -->
          <CalendarioHoy />

          <!-- Cargando TickTick (sin caché) -->
          <section v-if="!tickData && cargandoTick" class="md-card md-cargando">
            <span class="spinner-border spinner-border-sm"></span> Cargando tus tareas y hábitos…
          </section>

          <!-- Sin TickTick -->
          <section v-else-if="!tick.configured" class="md-card">
            <div class="md-empty">
              <i class="bi bi-plug"></i>
              <p class="mb-2">Conecta TickTick para ver aquí tus tareas y hábitos de hoy.</p>
              <router-link to="/perfil" class="btn btn-primary btn-sm">Conectar en Perfil</router-link>
            </div>
          </section>

          <template v-else>
            <div v-if="cargandoTick" class="md-actualizando"><span class="spinner-border spinner-border-sm"></span> Actualizando…</div>
            <div v-if="tick.errores && tick.errores.length" class="alert alert-warning py-2 small mb-0">
              <div v-for="e in tick.errores" :key="e">{{ e }}</div>
            </div>

            <!-- Tareas -->
            <section id="md-tareas" class="md-card">
              <div class="md-card__head">
                <h5><i class="bi bi-check2-square"></i>Tareas</h5>
                <span class="md-pill">{{ tareasPendientes.length }}</span>
              </div>
              <div v-if="!tareasPendientes.length" class="md-empty md-empty--ok">
                <i class="bi bi-emoji-sunglasses"></i>
                <p class="mb-0">Todo hecho por hoy. ¡Buen trabajo!</p>
              </div>
              <template v-else>
                <template v-for="grupo in gruposTareas" :key="grupo.clave">
                  <div v-if="grupo.items.length" class="md-grupo">{{ grupo.titulo }}</div>
                  <ul class="md-lista">
                    <li
                      v-for="t in grupo.items"
                      :key="t.id || t.titulo"
                      class="md-tarea"
                      :class="[prioridadBorde(t.prioridad), { 'is-saliendo': saliendo.has('t' + t.id) }]"
                    >
                      <button
                        type="button"
                        class="md-check"
                        :class="prioridadClase(t.prioridad)"
                        :aria-label="'Completar ' + t.titulo"
                        :disabled="enCurso.has('t' + t.id)"
                        @click="completarTarea(t)"
                      >
                        <span v-if="enCurso.has('t' + t.id)" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="bi bi-check-lg"></i>
                      </button>
                      <div class="md-tarea__cuerpo">
                        <div class="md-tarea__titulo">
                          <template v-for="(tr, i) in trocear(t.titulo)" :key="i">
                            <a v-if="tr.url" :href="tr.url" target="_blank" rel="noopener noreferrer">{{ tr.texto }}</a>
                            <template v-else>{{ tr.texto }}</template>
                          </template>
                        </div>
                        <div v-if="t.hora || t.atrasada" class="md-tarea__meta">
                          <span v-if="t.atrasada" class="md-etiqueta md-etiqueta--atrasada">Atrasada</span>
                          <span v-if="t.hora"><i class="bi bi-clock"></i> {{ t.hora }}</span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </template>
              </template>
            </section>

            <!-- Hábitos (el backend ya manda solo los no completados de hoy) -->
            <section id="md-habitos" class="md-card">
              <div class="md-card__head">
                <h5><i class="bi bi-lightning-charge"></i>Hábitos</h5>
                <span class="md-pill">{{ habitosPendientes.length }}</span>
              </div>
              <div v-if="tick.habitosCargando" class="md-actualizando">
                <span class="spinner-border spinner-border-sm"></span> Actualizando hábitos de hoy…
              </div>
              <div v-else-if="!habitosPendientes.length" class="md-empty md-empty--ok">
                <i class="bi bi-trophy"></i>
                <p class="mb-0">¡Todos los hábitos de hoy completados!</p>
              </div>
              <ul v-else class="md-lista">
                <li
                  v-for="h in habitosPendientes"
                  :key="h.id || h.nombre"
                  class="md-tarea md-habito"
                  :class="{ 'is-saliendo': saliendo.has('h' + h.id) }"
                >
                  <button
                    type="button"
                    class="md-check md-check--habito"
                    :aria-label="'Marcar ' + h.nombre"
                    :disabled="enCurso.has('h' + h.id)"
                    @click="marcarHabito(h)"
                  >
                    <span v-if="enCurso.has('h' + h.id)" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="bi bi-check-lg"></i>
                  </button>
                  <div class="md-tarea__cuerpo">
                    <div class="md-tarea__titulo">
                      <template v-for="(tr, i) in trocear(h.nombre)" :key="i">
                        <a v-if="tr.url" :href="tr.url" target="_blank" rel="noopener noreferrer">{{ tr.texto }}</a>
                        <template v-else>{{ tr.texto }}</template>
                      </template>
                    </div>
                  </div>
                  <span v-if="h.racha" class="md-racha" :title="'Racha de ' + h.racha + ' días'">🔥 {{ h.racha }}</span>
                </li>
              </ul>
            </section>

            <details v-if="tick.herramientas && tick.herramientas.length" class="md-herramientas">
              <summary>Herramientas del MCP de TickTick</summary>
              {{ tick.herramientas.join(', ') }}
            </details>
          </template>
        </div>

        <!-- Columna lateral -->
        <div class="col-lg-5 d-flex flex-column gap-3">
          <!-- Nutrición: lo que toca comer hoy (administrador, o quien tenga la tarjeta activada) -->
          <section v-if="puedeNutricion" class="md-card">
            <div class="md-card__head">
              <h5><i class="bi bi-egg-fried"></i>Hoy como</h5>
              <router-link to="/nutricion" class="small">Menú</router-link>
            </div>
            <ul v-if="platosHoy.length" class="md-lista">
              <li v-for="p in platosHoy" :key="p.momento" class="md-plato">
                <span class="md-plato__momento">{{ p.momento }}</span>
                <span class="md-plato__texto"><NutricionPlatoLink :texto="p.texto" :href="p.href" /></span>
              </li>
            </ul>
            <p v-else class="md-vacio">Aún no hay menú aceptado para hoy.</p>
          </section>

          <!-- Salud: solo si el usuario tiene alguna fuente conectada -->
          <section v-if="esAdmin || integraciones.salud" class="md-card">
            <div class="md-card__head">
              <h5><i class="bi bi-heart-pulse"></i>Salud de hoy</h5>
            </div>
            <p v-if="!tarjetasSalud.length" class="md-vacio">Aún no hay datos de salud actualizados hoy.</p>
            <div v-for="t in tarjetasSalud" :key="t.clave" class="md-salud" :class="'md-salud--' + t.clave">
              <div class="md-salud__nombre">{{ t.titulo }}</div>
              <div v-if="t.metricas.length" class="md-metricas">
                <div v-for="m in t.metricas" :key="m.label" class="md-metrica">
                  <div class="md-metrica__valor">{{ m.valor }}</div>
                  <div class="md-metrica__label">{{ m.label }}</div>
                </div>
              </div>
              <div v-for="l in t.lineas || []" :key="l" class="md-salud__linea">{{ l }}</div>
            </div>
            <p v-if="notaOura" class="md-nota"><i class="bi bi-info-circle me-1"></i>{{ notaOura }}</p>
          </section>

          <!-- Viendo ahora -->
          <section v-if="esAdmin || datos.peliculas.length" class="md-card">
            <div class="md-card__head">
              <h5><i class="bi bi-film"></i>Viendo ahora</h5>
              <router-link to="/peliculas" class="small">Ver todo</router-link>
            </div>
            <p v-if="!datos.peliculas.length" class="md-vacio">No estás viendo nada.</p>
            <ul v-else class="md-lista">
              <li v-for="p in datos.peliculas" :key="p.id" class="md-peli">
                <img v-if="p.poster_url" :src="poster(p.poster_url)" :alt="p.titulo" class="md-peli__poster" />
                <div v-else class="md-peli__poster md-peli__poster--vacio"><i class="bi bi-film"></i></div>
                <div>
                  <div class="fw-semibold">{{ p.titulo }}</div>
                  <small class="text-muted">
                    {{ [p.tipo === 'serie' ? 'Serie' : 'Película', p.anio, p.plataforma].filter(Boolean).join(' · ') }}
                  </small>
                </div>
              </li>
            </ul>
          </section>

          <FrasesDelDia v-if="esAdmin" compact :mostrar-enlace="false" />
        </div>
      </div>

      <!-- Chat -->
      <section v-if="esAdmin" class="md-card md-chat mt-3">
        <div class="md-card__head">
          <h5><i class="bi bi-stars"></i>Pregúntale a tu agente</h5>
          <button v-if="mensajes.length" class="btn btn-sm btn-link text-muted p-0" @click="borrarChat">Limpiar</button>
        </div>
        <div v-if="!datos.chat.llmConfigured" class="alert alert-warning small">
          Configura <code>DEEPSEEK_API_KEY</code> u <code>OPENAI_API_KEY</code> en el servidor para activar el chat.
        </div>
        <div ref="hilo" class="md-chat__hilo">
          <div v-if="!mensajes.length" class="md-chat__sugerencias">
            <button
              v-for="s in sugerencias"
              :key="s"
              type="button"
              class="md-sugerencia"
              :disabled="!datos.chat.llmConfigured"
              @click="enviarSugerencia(s)"
            >{{ s }}</button>
          </div>
          <div v-for="m in mensajes" :key="m.id" class="md-msg" :class="'md-msg--' + m.role">
            <div class="md-burbuja">{{ m.content }}</div>
          </div>
          <div v-if="enviando" class="md-msg">
            <div class="md-burbuja md-burbuja--pensando"><span></span><span></span><span></span></div>
          </div>
        </div>
        <form class="md-chat__form" @submit.prevent="enviar">
          <input
            v-model="texto"
            type="text"
            class="form-control"
            placeholder="Escribe tu pregunta…"
            maxlength="4000"
            :disabled="enviando || !datos.chat.llmConfigured"
          />
          <button class="btn btn-primary" :disabled="enviando || !texto.trim() || !datos.chat.llmConfigured" aria-label="Enviar">
            <i class="bi bi-send-fill"></i>
          </button>
        </form>
      </section>
    </template>
  </div>
</template>

<script>
import {
  computed, nextTick, onBeforeUnmount, onMounted, reactive, ref,
} from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import CalendarioHoy from '../components/CalendarioHoy.vue';
import FrasesDelDia from '../components/FrasesDelDia.vue';
import NutricionPlatoLink from '../components/NutricionPlatoLink.vue';
import { urlPoster } from '../utils/peliculasForm';
import {
  tarjetasSaludHoy, prioridadClase, notaOuraSinDatos, trocearTexto, saludo, leerCacheTick, guardarCacheTick,
  leerCacheLocal, guardarCacheLocal, CLAVE_CACHE_DATOS,
} from '../utils/miDia';

const SUGERENCIAS = [
  '¿Qué tengo pendiente hoy?',
  '¿Cómo he dormido esta semana?',
  '¿Cuánto he gastado este mes?',
  '¿Qué pedidos debo reclamar?',
];

export default {
  name: 'MiDia',
  components: { CalendarioHoy, FrasesDelDia, NutricionPlatoLink },
  setup() {
    const store = useStore();
    const datos = ref(leerCacheLocal(CLAVE_CACHE_DATOS));
    const esAdmin = computed(() => Boolean(store.getters['auth/isAdmin']));
    const tickData = ref(leerCacheTick());
    const correo = ref({ total: 0, sinLeer: 0, mas: false });
    const menuHoy = ref(null);
    const cargandoTick = ref(false);
    const cargando = ref(false);
    const error = ref('');
    const errorAccion = ref('');
    const mensajes = ref([]);
    const texto = ref('');
    const enviando = ref(false);
    const hilo = ref(null);
    const enCurso = reactive(new Set());
    const saliendo = reactive(new Set());
    const hechosLocal = reactive(new Set());

    const tick = computed(() => ({
      configured: false, tareas: [], habitos: [], eventos: [], errores: [], ...(tickData.value || {}),
    }));
    const tareasPendientes = computed(() => tick.value.tareas.filter((t) => !hechosLocal.has(`t${t.id}`)));
    const gruposTareas = computed(() => [
      { clave: 'atrasadas', titulo: 'Atrasadas', items: tareasPendientes.value.filter((t) => t.atrasada) },
      {
        clave: 'hoy',
        titulo: tareasPendientes.value.some((t) => t.atrasada) ? 'Para hoy' : '',
        items: tareasPendientes.value.filter((t) => !t.atrasada),
      },
    ]);
    const estaHecho = (h) => h.hecho || hechosLocal.has(`h${h.id}`);
    const habitosPendientes = computed(() => tick.value.habitos.filter((h) => !estaHecho(h)));
    const platosHoy = computed(() => {
      const d = menuHoy.value;
      if (!d) return [];
      return [
        { momento: 'Desayuno', texto: d.desayuno || '', href: d.desayunoHref || '' },
        { momento: 'Comida', texto: d.comida || '', href: d.comidaHref || '' },
        { momento: 'Cena', texto: d.cena || '', href: d.cenaHref || '' },
      ].filter((p) => p.texto.trim());
    });
    const puedeNutricion = computed(() => esAdmin.value || Boolean(datos.value?.puedeNutricion));
    const integraciones = computed(() => ({
      ticktick: false, calendar: false, gmail: false, salud: false, ...(datos.value?.integraciones || {}),
    }));
    const tarjetasSalud = computed(() => tarjetasSaludHoy(datos.value?.salud));
    const notaOura = computed(() => notaOuraSinDatos(datos.value?.salud));
    const nombre = computed(() => String(store.getters['auth/currentUser']?.nombre || '').split(' ')[0]);
    const saludoTexto = computed(() => saludo());
    const fechaLegible = computed(() => {
      const f = new Date().toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Madrid',
      });
      return f.charAt(0).toUpperCase() + f.slice(1);
    });

    // TickTick va aparte: es lo lento (varias llamadas al MCP) y no debe bloquear el resto de la vista.
    // El servidor responde al instante con su última copia (marcada "obsoleto") y refresca detrás:
    // se vuelve a consultar en unos segundos para sustituirla por la fresca, sin que el usuario espere.
    let temporizadorTick = null;
    let reintentosTick = 0;
    async function cargarTick(refrescar = false) {
      clearTimeout(temporizadorTick);
      cargandoTick.value = true;
      let reprogramado = false;
      try {
        const { data } = await axios.get('/api/mi-dia/ticktick', { params: refrescar ? { refrescar: 1 } : {} });
        tickData.value = data;
        hechosLocal.clear();
        if (!data.errores?.length) guardarCacheTick(data);
        if ((data.obsoleto || data.habitosCargando) && reintentosTick < 6) {
          reintentosTick += 1;
          reprogramado = true;
          temporizadorTick = setTimeout(() => cargarTick(false), 3000);
        } else {
          reintentosTick = 0;
        }
      } catch (e) {
        errorAccion.value = e.response?.data?.error || 'No se pudo cargar TickTick';
      } finally {
        cargandoTick.value = reprogramado;
      }
    }

    async function cargarResto() {
      cargando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/mi-dia');
        datos.value = data;
        guardarCacheLocal(CLAVE_CACHE_DATOS, data);
        if (puedeNutricion.value) cargarMenu();
      } catch (e) {
        // Con copia local se sigue mostrando; solo se avisa si no hay nada que enseñar.
        if (!datos.value) error.value = e.response?.data?.error || 'No se pudo cargar Mi día';
      } finally {
        cargando.value = false;
      }
    }

    // Extras que no bloquean la vista: si fallan, simplemente no aparecen.
    async function cargarCorreo(refrescar = false) {
      try {
        const { data } = await axios.get('/api/mi-dia/correo', { params: refrescar ? { refrescar: 1 } : {} });
        correo.value = data;
      } catch {
        correo.value = { total: 0, sinLeer: 0, mas: false };
      }
    }

    async function cargarMenu() {
      try {
        const { data } = await axios.get('/api/nutricion/menu-activo');
        menuHoy.value = (data.dias || []).find((d) => d.fecha === data.hoy) || null;
      } catch {
        menuHoy.value = null;
      }
    }

    function cargar(refrescar = false) {
      // El correo es del administrador (Gmail); el menú se pide al llegar los datos, si el usuario tiene la tarjeta.
      return Promise.all([
        cargarResto(),
        cargarTick(refrescar),
        esAdmin.value ? cargarCorreo(refrescar) : null,
      ]);
    }

    // El servidor escribe en TickTick, lo comprueba y devuelve los datos frescos.
    // Solo si es de verdad se anima la salida del elemento y se sustituyen los datos.
    async function accion(clave, peticion, fallo) {
      if (enCurso.has(clave)) return;
      errorAccion.value = '';
      enCurso.add(clave);
      try {
        const { data } = await peticion();
        saliendo.add(clave);
        await new Promise((r) => setTimeout(r, 280));
        if (data?.ticktick) {
          tickData.value = data.ticktick;
          hechosLocal.clear();
          if (!data.ticktick.errores?.length) guardarCacheTick(data.ticktick);
        } else {
          hechosLocal.add(clave);
        }
      } catch (e) {
        errorAccion.value = e.response?.data?.error || fallo;
      } finally {
        enCurso.delete(clave);
        saliendo.delete(clave);
      }
    }

    const completarTarea = (t) => accion(
      `t${t.id}`,
      () => axios.post('/api/mi-dia/ticktick/tareas/completar', { id: t.id, proyectoId: t.proyectoId }),
      'No se pudo completar la tarea'
    );

    const marcarHabito = (h) => accion(
      `h${h.id}`,
      () => axios.post('/api/mi-dia/ticktick/habitos/marcar', { id: h.id, objetivo: h.objetivo }),
      'No se pudo marcar el hábito'
    );

    async function bajarHilo() {
      await nextTick();
      if (hilo.value) hilo.value.scrollTop = hilo.value.scrollHeight;
    }

    async function cargarHistorial() {
      try {
        const { data } = await axios.get('/api/mi-dia/chat/history');
        mensajes.value = data.messages || [];
        bajarHilo();
      } catch {
        mensajes.value = [];
      }
    }

    async function enviar() {
      const mensaje = texto.value.trim();
      if (!mensaje || enviando.value) return;
      texto.value = '';
      enviando.value = true;
      mensajes.value.push({ id: `u${Date.now()}`, role: 'user', content: mensaje });
      bajarHilo();
      try {
        const { data } = await axios.post('/api/mi-dia/chat', { message: mensaje });
        mensajes.value.push({ id: `a${Date.now()}`, role: 'assistant', content: data.reply });
      } catch (e) {
        mensajes.value.push({
          id: `e${Date.now()}`,
          role: 'assistant',
          content: e.response?.data?.error || 'El agente no pudo responder.',
        });
      } finally {
        enviando.value = false;
        bajarHilo();
      }
    }

    function enviarSugerencia(s) {
      texto.value = s;
      enviar();
    }

    async function borrarChat() {
      try {
        await axios.delete('/api/mi-dia/chat/history');
        mensajes.value = [];
      } catch { /* se mantiene el historial */ }
    }

    const prioridadBorde = (p) => (p >= 5 ? 'md-p-alta' : p >= 3 ? 'md-p-media' : p >= 1 ? 'md-p-baja' : '');

    onMounted(() => {
      cargar();
      if (esAdmin.value) cargarHistorial();
    });
    onBeforeUnmount(() => clearTimeout(temporizadorTick));

    return {
      datos, esAdmin, puedeNutricion, integraciones, cargando, cargandoTick, correo, platosHoy, error, errorAccion, tick, tareasPendientes, gruposTareas, habitosPendientes,
      tarjetasSalud, notaOura, nombre, saludoTexto, fechaLegible,
      mensajes, texto, enviando, hilo, enCurso, saliendo, sugerencias: SUGERENCIAS,
      cargar, enviar, enviarSugerencia, borrarChat, completarTarea, marcarHabito,
      poster: urlPoster, prioridadClase, prioridadBorde, trocear: trocearTexto,
    };
  },
};
</script>

<style scoped>
.mi-dia { max-width: 1280px; margin-left: auto; margin-right: auto; padding-bottom: calc(var(--app-tabbar-offset, 5rem) + 1rem); }

/* Cabecera */
.md-hero {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
  padding: 1.25rem 1.25rem 1.1rem; margin-bottom: 0.9rem; border-radius: 1rem; color: #fff;
  background: linear-gradient(135deg, var(--marca, #1f6b45), var(--marca-oscura, #163528));
}
.md-hero__title { font-size: 1.6rem; font-weight: 700; margin: 0; }
.md-hero__fecha { margin: 0.15rem 0 0; opacity: 0.85; }
.md-refresh { border-radius: 999px; }

/* Chips de resumen */
.md-chips { display: flex; gap: 0.5rem; overflow-x: auto; padding-bottom: 0.25rem; margin-bottom: 0.9rem; scrollbar-width: none; }
.md-chips::-webkit-scrollbar { display: none; }
.md-chip {
  display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap; flex: 0 0 auto;
  padding: 0.45rem 0.85rem; border-radius: 999px; background: #fff; color: var(--tinta, #1c241c);
  border: 1px solid var(--linea, rgba(22, 53, 40, 0.12)); text-decoration: none; font-size: 0.92rem;
}
.md-chip i { color: var(--marca, #1f6b45); }
.md-chip:hover { border-color: var(--marca, #1f6b45); }
.md-chip__alerta { background: var(--urgente, #b42318); color: #fff; border-radius: 999px; padding: 0 0.5rem; font-size: 0.75rem; }

/* Tarjetas */
.md-card {
  background: #fff; border-radius: 1rem; padding: 1rem 1.1rem;
  border: 1px solid var(--linea, rgba(22, 53, 40, 0.12)); box-shadow: 0 1px 2px rgba(22, 53, 40, 0.04);
}
.md-card__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
.md-card__head h5 { margin: 0; font-size: 1.05rem; font-weight: 700; display: flex; align-items: center; gap: 0.5rem; }
.md-card__head h5 i { color: var(--marca, #1f6b45); }
.md-pill { background: var(--marca-suave, rgba(31, 107, 69, 0.12)); color: var(--marca, #1f6b45); border-radius: 999px; padding: 0.1rem 0.6rem; font-weight: 600; font-size: 0.85rem; }
.md-vacio { color: var(--tinta-suave, #5c675e); margin: 0.25rem 0; font-size: 0.92rem; }
.md-empty { text-align: center; padding: 1rem 0.5rem; color: var(--tinta-suave, #5c675e); }
.md-empty i { font-size: 1.8rem; display: block; margin-bottom: 0.3rem; }
.md-empty--ok i { color: var(--marca, #1f6b45); }

/* Listas */
.md-lista { list-style: none; margin: 0; padding: 0; }
.md-grupo { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--tinta-suave, #5c675e); margin: 0.6rem 0 0.2rem; font-weight: 700; }
.md-tarea {
  display: flex; align-items: center; gap: 0.7rem; padding: 0.55rem 0.5rem; border-radius: 0.7rem;
  border-left: 3px solid transparent; transition: opacity 0.28s, transform 0.28s, background 0.15s, max-height 0.28s;
  max-height: 12rem;
}
.md-tarea:hover { background: var(--papel, #f4f6f3); }
.md-tarea.is-saliendo { opacity: 0; transform: translateX(14px); }
.md-p-alta { border-left-color: var(--urgente, #b42318); }
.md-p-media { border-left-color: var(--aviso, #b45309); }
.md-p-baja { border-left-color: #2563eb; }
.md-tarea__cuerpo { flex: 1; min-width: 0; }
.md-tarea__titulo { overflow-wrap: anywhere; line-height: 1.3; }
.md-tarea__titulo a { color: var(--marca, #1f6b45); }
.md-tarea__meta { display: flex; gap: 0.5rem; align-items: center; font-size: 0.8rem; color: var(--tinta-suave, #5c675e); margin-top: 0.1rem; }
.md-etiqueta--atrasada { background: rgba(180, 35, 24, 0.1); color: var(--urgente, #b42318); border-radius: 999px; padding: 0 0.5rem; font-weight: 600; }

/* Botón circular de completar */
.md-check {
  flex: 0 0 auto; width: 1.7rem; height: 1.7rem; border-radius: 50%; border: 2px solid currentColor;
  background: transparent; color: var(--tinta-suave, #5c675e); display: inline-flex; align-items: center; justify-content: center;
  padding: 0; transition: background 0.15s, color 0.15s, transform 0.15s;
}
.md-check i { opacity: 0; font-size: 0.95rem; transition: opacity 0.15s; }
.md-check:hover:not(:disabled), .md-check:focus-visible { background: currentColor; transform: scale(1.08); }
.md-check:hover:not(:disabled) i, .md-check:focus-visible i { opacity: 1; color: #fff; }
.md-check.text-danger { color: var(--urgente, #b42318) !important; }
.md-check.text-warning { color: var(--aviso, #b45309) !important; }
.md-check.text-primary { color: #2563eb !important; }
.md-check--habito { color: var(--marca, #1f6b45); }
.md-racha { font-size: 0.85rem; color: var(--tinta-suave, #5c675e); white-space: nowrap; }

/* Progreso de hábitos */
.md-progreso { height: 0.45rem; background: var(--marca-suave, rgba(31, 107, 69, 0.12)); border-radius: 999px; overflow: hidden; margin-bottom: 0.4rem; }
.md-progreso__barra { height: 100%; background: var(--marca, #1f6b45); border-radius: 999px; transition: width 0.4s; }

.md-cargando { color: var(--tinta-suave, #5c675e); display: flex; align-items: center; gap: 0.6rem; }
.md-actualizando { font-size: 0.8rem; color: var(--tinta-suave, #5c675e); display: flex; align-items: center; gap: 0.4rem; }
.md-herramientas { font-size: 0.78rem; color: var(--tinta-suave, #5c675e); }

/* Salud */
.md-salud { padding: 0.7rem 0.8rem; border-radius: 0.8rem; background: var(--papel, #f4f6f3); margin-bottom: 0.6rem; }
.md-salud__nombre { font-weight: 700; font-size: 0.85rem; margin-bottom: 0.45rem; color: var(--marca-oscura, #163528); }
.md-metricas { display: grid; grid-template-columns: repeat(auto-fill, minmax(5.6rem, 1fr)); gap: 0.5rem; }
.md-metrica__valor { font-size: 1.15rem; font-weight: 700; line-height: 1.1; font-family: var(--fuente-cifra, Georgia, serif); }
.md-metrica__label { font-size: 0.74rem; color: var(--tinta-suave, #5c675e); }
.md-salud__linea { font-size: 0.9rem; padding: 0.15rem 0; }
.md-nota { font-size: 0.82rem; color: var(--tinta-suave, #5c675e); margin: 0.3rem 0 0; }

.md-intro { background: linear-gradient(135deg, rgba(31, 107, 69, 0.08), rgba(31, 107, 69, 0.02)); margin-bottom: 0.9rem; }

/* Nutrición */
.md-plato { display: flex; gap: 0.75rem; align-items: baseline; padding: 0.4rem 0.2rem; border-bottom: 1px solid var(--linea, rgba(22, 53, 40, 0.12)); }
.md-plato:last-child { border-bottom: 0; }
.md-plato__momento { flex: 0 0 4.2rem; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--tinta-suave, #5c675e); font-weight: 700; }
.md-plato__texto { flex: 1; min-width: 0; overflow-wrap: anywhere; }

/* Películas */
.md-peli { display: flex; gap: 0.7rem; align-items: center; padding: 0.35rem 0; }
.md-peli__poster { width: 40px; height: 60px; object-fit: cover; border-radius: 6px; flex: 0 0 auto; }
.md-peli__poster--vacio { display: flex; align-items: center; justify-content: center; background: var(--papel, #f4f6f3); color: var(--tinta-suave, #5c675e); }

/* Chat */
.md-chat__hilo { max-height: 340px; overflow-y: auto; display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }
.md-chat__sugerencias { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.md-sugerencia {
  border: 1px solid var(--linea, rgba(22, 53, 40, 0.12)); background: var(--papel, #f4f6f3); border-radius: 999px;
  padding: 0.4rem 0.8rem; font-size: 0.88rem; color: var(--tinta, #1c241c);
}
.md-sugerencia:hover:not(:disabled) { border-color: var(--marca, #1f6b45); color: var(--marca, #1f6b45); }
.md-msg { display: flex; }
.md-msg--user { justify-content: flex-end; }
.md-burbuja { max-width: 88%; padding: 0.55rem 0.85rem; border-radius: 1rem; white-space: pre-wrap; background: var(--papel, #f4f6f3); }
.md-msg--user .md-burbuja { background: var(--marca, #1f6b45); color: #fff; border-bottom-right-radius: 0.3rem; }
.md-msg--assistant .md-burbuja { border-bottom-left-radius: 0.3rem; }
.md-burbuja--pensando { display: inline-flex; gap: 0.25rem; }
.md-burbuja--pensando span { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: var(--tinta-suave, #5c675e); animation: md-pulso 1s infinite ease-in-out; }
.md-burbuja--pensando span:nth-child(2) { animation-delay: 0.15s; }
.md-burbuja--pensando span:nth-child(3) { animation-delay: 0.3s; }
@keyframes md-pulso { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }
.md-chat__form { display: flex; gap: 0.5rem; }

@media (max-width: 575.98px) {
  .md-hero { padding: 1rem; border-radius: 0.9rem; }
  .md-hero__title { font-size: 1.35rem; }
  .md-card { padding: 0.9rem; }
}
@media (prefers-reduced-motion: reduce) {
  .md-tarea, .md-check, .md-progreso__barra { transition: none; }
  .md-burbuja--pensando span { animation: none; }
}
</style>
